import { FC } from "react";
import { IconButton, Tooltip } from "@mui/material";
import SvgIcon from "@mui/material/SvgIcon";
import Checkbox from "@mui/material/Checkbox";

import "./style.scss";
import { ReactComponent as CheckboxIcon } from "../../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../../assets/checkbox-checked.icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete.icon.svg";
import { ReactComponent as LinkIcon } from "../../../assets/link.icon.svg";
import { ReactComponent as CopyIcon } from "../../../assets/copy.icon.svg";
import { ReactComponent as GearIcon } from "../../../assets/gear.icon.svg";
import { ReactComponent as SmilePlaceholder } from "../../../assets/placeholder--sad-smile.icon.svg";
import { Tenant } from "../../../common/interfaces/admin/tenant.interface";

const statusDisplay: Record<string, { label: string; color: string }> = {
    active: { label: "Active", color: "green" },
    deleting_users: { label: "Deleting Users...", color: "orange" },
    deleting_knowledge_bases: { label: "Deleting KBs...", color: "orange" },
    deleting_firebase_tenant: { label: "Deleting Auth...", color: "orange" }
};

const TenantsTable = ({
    items,
    checkedItems,
    setCheckedItems,
    handleDelete,
    handleCopyLink,
    handleCopyAdminInvite,
    handleProperties,
    placeholderIcon = SmilePlaceholder,
    placeholderDescription = "No tenants found"
}: {
    items: Tenant[];
    checkedItems: Tenant[];
    setCheckedItems: (arg1: Tenant[]) => void;
    handleDelete?: (arg1: number) => void;
    handleCopyLink?: (arg1: Tenant) => void;
    handleCopyAdminInvite?: (arg1: Tenant) => void;
    handleProperties?: (arg1: Tenant) => void;
    placeholderIcon?: FC<React.SVGProps<SVGSVGElement>>;
    placeholderDescription?: string;
}) => {
    const isChecked = (tenant: Tenant) => checkedItems.some(item => item.id === tenant.id);

    const toggleCheck = (tenant: Tenant) => {
        if (isChecked(tenant)) {
            setCheckedItems(checkedItems.filter(item => item.id !== tenant.id));
        } else {
            setCheckedItems([...checkedItems, tenant]);
        }
    };

    const toggleAll = () => {
        if (checkedItems.length === items.length) {
            setCheckedItems([]);
        } else {
            setCheckedItems([...items]);
        }
    };

    const isError = (tenant: Tenant) => tenant.status.startsWith("error");
    // Error rows are NOT "deleting": the backend accepts a delete retry
    // from an error status, so their actions stay enabled.
    const isDeleting = (tenant: Tenant) => tenant.status !== "active" && !isError(tenant);

    const getStatusInfo = (tenant: Tenant) => {
        if (isError(tenant)) {
            return { label: "Error", color: "red" };
        }
        return statusDisplay[tenant.status] || { label: tenant.status, color: "default" };
    };

    return (
        <div className="tenant-table-view">
            {items.length > 0 ? (
                <div className="tenant-table" role="table" aria-label="Tenants">
                    <div className="tenant-table-header" role="row">
                        <div
                            className="tenant-table-cell tenant-table-cell__select"
                            role="columnheader"
                        >
                            <Checkbox
                                icon={<SvgIcon component={CheckboxIcon} inheritViewBox />}
                                checkedIcon={
                                    <SvgIcon component={CheckboxCheckedIcon} inheritViewBox />
                                }
                                checked={checkedItems.length === items.length && items.length > 0}
                                onChange={toggleAll}
                                inputProps={{ "aria-label": "Select all tenants" }}
                            />
                        </div>
                        <div
                            className="tenant-table-cell tenant-table-cell__name"
                            role="columnheader"
                        >
                            Name
                        </div>
                        <div
                            className="tenant-table-cell tenant-table-cell__display-name"
                            role="columnheader"
                        >
                            Display Name
                        </div>
                        <div
                            className="tenant-table-cell tenant-table-cell__subdomain"
                            role="columnheader"
                        >
                            Subdomain
                        </div>
                        <div
                            className="tenant-table-cell tenant-table-cell__tenant-id"
                            role="columnheader"
                        >
                            Tenant ID
                        </div>
                        <div
                            className="tenant-table-cell tenant-table-cell__status"
                            role="columnheader"
                        >
                            Status
                        </div>
                        <div
                            className="tenant-table-cell tenant-table-cell__action"
                            role="columnheader"
                        >
                            Actions
                        </div>
                    </div>
                    <div className="tenant-table-body" role="rowgroup">
                        {items.map(tenant => {
                            const statusInfo = getStatusInfo(tenant);
                            const deleting = isDeleting(tenant);
                            return (
                                <div key={tenant.id} className="tenant-table-row" role="row">
                                    <div
                                        className="tenant-table-cell tenant-table-cell__select"
                                        role="cell"
                                    >
                                        <Checkbox
                                            icon={
                                                <SvgIcon component={CheckboxIcon} inheritViewBox />
                                            }
                                            checkedIcon={
                                                <SvgIcon
                                                    component={CheckboxCheckedIcon}
                                                    inheritViewBox
                                                />
                                            }
                                            checked={isChecked(tenant)}
                                            onChange={() => toggleCheck(tenant)}
                                            inputProps={{
                                                "aria-label": `Select ${tenant.name}`
                                            }}
                                        />
                                    </div>
                                    <div
                                        className="tenant-table-cell tenant-table-cell__name"
                                        role="rowheader"
                                    >
                                        <span className="tenant-cell-text">{tenant.name}</span>
                                    </div>
                                    <div
                                        className="tenant-table-cell tenant-table-cell__display-name"
                                        role="cell"
                                    >
                                        <span className="tenant-cell-text">
                                            {tenant.displayName}
                                        </span>
                                    </div>
                                    <div
                                        className="tenant-table-cell tenant-table-cell__subdomain"
                                        role="cell"
                                    >
                                        <span className="tenant-cell-text">{tenant.subdomain}</span>
                                    </div>
                                    <div
                                        className="tenant-table-cell tenant-table-cell__tenant-id"
                                        role="cell"
                                    >
                                        <span className="tenant-cell-text">
                                            {tenant.tenantId || "—"}
                                        </span>
                                    </div>
                                    <div
                                        className="tenant-table-cell tenant-table-cell__status"
                                        role="cell"
                                    >
                                        <Tooltip
                                            title={
                                                isError(tenant)
                                                    ? tenant.status.replace("error: ", "")
                                                    : ""
                                            }
                                        >
                                            <span
                                                className={`tenant-status tenant-status--${statusInfo.color}`}
                                            >
                                                {statusInfo.label}
                                            </span>
                                        </Tooltip>
                                    </div>
                                    <div
                                        className="tenant-table-cell tenant-table-cell__action"
                                        role="cell"
                                    >
                                        <Tooltip title="Delete Tenant">
                                            <span>
                                                <IconButton
                                                    className="tenant-action-button"
                                                    aria-label={`Delete Tenant ${tenant.name}`}
                                                    disabled={deleting}
                                                    onClick={() => handleDelete?.(tenant.id)}
                                                >
                                                    <SvgIcon
                                                        component={DeleteIcon}
                                                        inheritViewBox
                                                    />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Copy Tenant Link">
                                            <span>
                                                <IconButton
                                                    className="tenant-action-button"
                                                    aria-label={`Copy Tenant Link for ${tenant.name}`}
                                                    disabled={deleting}
                                                    onClick={() => handleCopyLink?.(tenant)}
                                                >
                                                    <SvgIcon component={LinkIcon} inheritViewBox />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Copy Admin Invite Link">
                                            <span>
                                                <IconButton
                                                    className="tenant-action-button"
                                                    aria-label={`Copy Admin Invite Link for ${tenant.name}`}
                                                    disabled={deleting}
                                                    onClick={() => handleCopyAdminInvite?.(tenant)}
                                                >
                                                    <SvgIcon component={CopyIcon} inheritViewBox />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                        <Tooltip title="Properties">
                                            <span>
                                                <IconButton
                                                    className="tenant-action-button"
                                                    aria-label={`Properties for ${tenant.name}`}
                                                    disabled={deleting}
                                                    onClick={() => handleProperties?.(tenant)}
                                                >
                                                    <SvgIcon component={GearIcon} inheritViewBox />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div className="tenant-table-placeholder">
                    <SvgIcon
                        className="tenant-table-placeholder__icon"
                        component={placeholderIcon}
                        inheritViewBox
                    />
                    <span className="tenant-table-placeholder__text">{placeholderDescription}</span>
                </div>
            )}
        </div>
    );
};

export default TenantsTable;
